import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { isAutoSignature } from '../formDetail/signatureFill.constants';
import { attributeTypes, Images, sanitizeHtml } from 'utils';
import {
    useFormBuilder,
    GraphElement,
    TargetElement,
    GraphCharts,
    SessionDataTableElement,
    AutoSignatureElement,
} from '../formDetail';
import { PrevRadioElement, PrevCheckboxElement, PrevTableElement, PrevFileSignature, PrevInfoLine, PrevUploadedFile, PrevHeaderFooterBlock} from './elements';
import { getWidth } from '../constants';
import { NoItemsYet } from 'components';
import { evaluateConditions } from '../utils/evaluateConditions';
import { goalAttributes } from '../utils/goalAttributes';
import { resolveRichTextImages } from '../utils/resolveRichTextImages';
import { hasInlineFields } from '../utils/inlineFields';
import { InlineFieldsRichText } from '../formDetail/formBody/elements/inlineFieldsRichText';
import { sectionTitleStyle, showSectionTitle, isContinuousSections } from '../formDetail/section';

export const FormPreview = () => {
    const { formList, imageUrls } = useFormBuilder();
    const { watch, setValue, getValues, reset } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });

    useEffect(() => {
        if (formList) {
            reset({
                customForm: formList?.id,
                formId: formList?.id,
                ...formList,
            });
        }
    }, [formList]);

    const renderInputs = (item, index, sectionIndex, goalId = null, targetId = null) => {
        const sectionGoals = watch(`sections.${sectionIndex}.goals`) || {};
        let goalAttrs;
        if (goalId && targetId) {
            goalAttrs =
                sectionGoals?.[goalId]?.targets?.[targetId]?.attributes?.[index] ||
                formList?.goals?.[goalId]?.targets?.[targetId]?.attributes?.[index] ||
                {};
        } else if (goalId && !sectionGoals?.[goalId]?.ownAttributes) {
            goalAttrs = sectionGoals?.[goalId]?.attributes?.[index] || formList?.goals?.[goalId]?.attributes?.[index] || {};
        } else {
            // No goal, or a goal that owns its fields: `item` is already complete.
            goalAttrs = {};
        }
        const goalItem = goalId && item ? { ...item, ...goalAttrs } : item;
        const isGoalSection = !!goalId;
        const sharedProps = {
            item: goalItem,
            index,
            value: goalItem?.value,
            readOnly: true,
            formList,
            onChange: () => {},
        };

        switch (goalItem?.type) {
            case attributeTypes.INPUT:
                return <PrevInfoLine {...sharedProps} />;
            case attributeTypes.SELECT:
                return <PrevInfoLine {...sharedProps} />;

            case attributeTypes.CHECK_BOX:
                return <PrevCheckboxElement {...sharedProps} />;
            case attributeTypes.RADIO_BUTTON:
                return <PrevRadioElement {...sharedProps} />;

            case attributeTypes.TABLE:
                return <PrevTableElement {...sharedProps} />;

            case attributeTypes.SESSION_DATA_TABLE:
                return <SessionDataTableElement {...sharedProps} />;

            case attributeTypes.RICH_TEXT:
                // A template has no values yet: inline fields preview as empty, disabled controls.
                return hasInlineFields(goalItem?.value) ? (
                    <InlineFieldsRichText html={goalItem?.value} preview imageUrls={imageUrls} />
                ) : (
                    <div className="richtext-readonly-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolveRichTextImages(goalItem?.value, imageUrls)) }} />
                );

            case attributeTypes.UPLOAD:
                return <PrevUploadedFile {...sharedProps} />;

            case attributeTypes.SIGNATURE:
                return isAutoSignature(goalItem?.properties) ? (
                    <AutoSignatureElement item={goalItem} />
                ) : (
                    <PrevFileSignature {...sharedProps} />
                );

            case attributeTypes.GRAPH:
                return isGoalSection ? (
                    <GraphCharts item={goalItem} sectionIndex={sectionIndex} goalId={goalId} />
                ) : (
                    <GraphElement {...sharedProps} />
                );
            case attributeTypes.TARGET:
                return (
                    <TargetElement
                        {...sharedProps}
                        isReportBuilder={isGoalSection}
                        sectionIndex={sectionIndex}
                        goalId={goalId}
                        targetId={targetId}
                        imageUrls={imageUrls || {}}
                    />
                );

            default:
                return null;
        }
    };

    const renderSectionAttributes = (section, sIndex, goalId = null, goal = null) => {
        // A goal block renders that goal's own fields when it owns them.
        const ownList = goalId && section?.goals?.[goalId]?.ownAttributes;
        const sectionAttributes = (ownList ? goalAttributes(watch()?.sections, sIndex, goalId) : section?.attributes) || [];

        return sectionAttributes.map((item, index) => {
            // Evaluate conditional logic for this attribute
            if (item?.properties?.conditions?.length) {
                const { visible } = evaluateConditions(item.properties.conditions, sectionAttributes);
                if (!visible) return null;
            }

            if (item?.type === attributeTypes.TARGET && goalId && goal?.targets?.length) {
                return goal.targets.map((target) => {
                    const width = getWidth(item?.properties?.width);
                    return (
                        <div key={`${goalId}-target-${target?.id}-${index}`} className="form-preview-item" style={{ width: '100%' }}>
                            <div>
                                <div>
                                    <p className="preview-info-line-title">{item?.label}</p>
                                </div>
                                <div className="preview-input-wrapper">{renderInputs(item, index, sIndex, goalId, target?.id)}</div>
                            </div>
                        </div>
                    );
                });
            }

            const width = getWidth(item?.properties?.width);
            const isFullWidthType = [
                attributeTypes.RICH_TEXT,
                attributeTypes.TABLE,
                attributeTypes.SESSION_DATA_TABLE,
                attributeTypes.GRAPH,
                attributeTypes.TARGET,
            ].includes(item?.type);
            return (
                <div
                    key={goalId ? `${goalId}-${item.id || index}` : item.id || index}
                    className="form-preview-item"
                    style={{
                        width: isFullWidthType ? '100%' : width,
                    }}
                >
                    <div>
                        <div>
                            <p className="preview-info-line-title">{item?.label}</p>
                        </div>
                        <div className="preview-input-wrapper">{renderInputs(item, index, sIndex, goalId)}</div>
                    </div>
                </div>
            );
        });
    };

    // Matches the filled form: continuous (the default) puts every section on one
    // sheet with a single header and footer; off gives each section its own page.
    const continuousSections = isContinuousSections(formList?.formSettings);
    // An empty section fills its own page; on the continuous sheet that height would leave a gap mid-page.
    const emptySectionStyle = continuousSections
        ? { position: 'relative', height: 'auto', padding: '24px 0' }
        : { position: 'relative', height: '83vh' };
    const wrapSectionsInPage = (sectionNodes) =>
        continuousSections ? (
            <div className="form-preview-page" style={{ padding: 0, gap: 0 }}>
                <PrevHeaderFooterBlock type="header" data={formList?.header} pageIndex={0} totalPages={1} />
                {sectionNodes}
                <PrevHeaderFooterBlock type="footer" data={formList?.footer} pageIndex={0} totalPages={1} />
            </div>
        ) : (
            sectionNodes
        );

    return (
        <div className="form-preview-container">
            <form>
                {wrapSectionsInPage(watch()?.sections?.map((section, sIndex) => {
                    const totalSections = watch()?.sections?.length || 1;

                    if (!section?.attributes?.length) return (
                        <div key={sIndex}>
                            <NoItemsYet
                                containerStyles={emptySectionStyle}
                                text={'No Selected Attributes Yet'}
                                subTitle={'Start by adding attributes to this section to see a preview of them here.'}
                                image={Images.noDashboard}
                            />
                        </div>
                    );

                    return (
                        <div
                            key={section.id || sIndex}
                            className={continuousSections ? 'form-preview-continuous-section' : 'form-preview-page'}
                            style={continuousSections ? undefined : { padding: 0, gap: 0 }}
                        >

                            {/* Header */}
                            {!continuousSections && (
                                <PrevHeaderFooterBlock
                                    type="header"
                                    data={formList?.header}
                                    pageIndex={sIndex}
                                    totalPages={totalSections}
                                />
                            )}

                            <div className="form-preview-section">
                                {showSectionTitle(section) && (
                                    <div className="form-preview-section-heading">
                                        <p className="form-builder-section-title" style={sectionTitleStyle(section)}>
                                            {section?.name}
                                        </p>
                                        <div className="form-builder-section-line" />
                                    </div>
                                )}
                                {!section?.goalSection && (
                                    <div className="form-preview-attributes">{renderSectionAttributes(section, sIndex)}</div>
                                )}

                                {section?.goalSection &&
                                    section?.selectedGoals?.length > 0 &&
                                    section?.selectedGoals?.map((goal) => (
                                        <div key={goal?.id} className="form-preview-goal-block" style={{ marginBottom: 24 }}>

                                            <p className="form-preview-goal-title">
                                                {goal?.name || 'Goal'}
                                            </p>
                                            <div className="form-preview-attributes">
                                                {renderSectionAttributes(section, sIndex, goal?.id, goal)}
                                            </div>
                                        </div>
                                    ))}

                                {section?.goalSection && (!section?.selectedGoals || section?.selectedGoals?.length === 0) && (
                                    <div className="form-preview-attributes">{renderSectionAttributes(section, sIndex)}</div>
                                )}
                            </div>

                            {/* Footer */}
                            {!continuousSections && (
                                <PrevHeaderFooterBlock
                                    type="footer"
                                    data={formList?.footer}
                                    pageIndex={sIndex}
                                    totalPages={totalSections}
                                />
                            )}
                        </div>
                    );
                }))}
            </form>
        </div>
    );
};
